/* eslint-disable max-statements */
/* eslint-disable new-cap */
import { Accessories } from '../accessories/accessories.class'
import { Artifacts } from '../artifacts/artifacts.class';
import { BlueprintContext } from '..';
import { BlueprintInstanceType } from '../shared.enums';
import { kitchenData } from './kitchen.data';
import { KitchenDesign } from '../kitchen-design/kitchen-design.class';
import { kitchenLayouts } from './data';
import { KitchenMaterial } from '../kitchen-material/kitchen-material.class';
import { Notes } from '../../note-classes/notes.class';
import { PromotionalSkus } from '../promotional-skus/promotional-skus.class';
import { SkuAnnotations } from '../sku-annotations/sku-annotations.class';
import { Publish } from '../../decorators';
import { RulesEngine } from '../rules-engine';
import { toCamelCase } from '../../helpers';
import { Vertex3 } from '../../spatial-classes';

import { BlueprintBase, BlueprintOptions } from '../blueprint-base';
import { emptyInternalReferenceMapCfn, emptyReferenceMapCfn, resolveReferenceMapCfn } from '../kitchen-design-base/functions';
import { getDescriptionCfn, getEstimateLineItemsCfn, getItemizedMsrpCfn, getSizeCfn, getTrimProfileCfn, rebindFuelVariantsCfn, setTrimProfileCfn } from './functions';
import { InternalReferenceMap, ReferenceMap, ReferenceMapItemType } from '../shared.types';
import { DesignDetailsData, KitchenDataIn, KitchenDataOut, KitchenEstimateLineItem, KitchenItemizedMsrp, KitchenLayouts, KitchenSize, KitchenTrimProfile } from './types';
import type { PriceBookPinnedBy } from './types';

export class Kitchen extends BlueprintBase<KitchenDataIn> {
	#id: string;
	#baseVersion: number | null;
	#version: number | null;
	#createdBy: string;
	#createDate: string;
	#fuelchoice: string;
	#skyKey: string;
	#floorKey: string;
	#designPackageImage: string;
	#rulesEngine: RulesEngine;
	#description: string;
	#kitchenLayout: string;
	#layout: KitchenLayouts;
	#location: string;
	#tax: number;
	#quoteId: string;
	#isFinalized: boolean;
	#dealId: string;
	#priceBookVersionId: string | null;
	#priceBookPinnedBy: PriceBookPinnedBy | null;
	#priceBookRuleId: string | null;
	#designDetails: DesignDetailsData | null;
	#blueprintOverrides: Record<string, string>;
	#blueprintOverrideBases: Record<string, string>;

	readonly notes: Notes;
	readonly accessories: Accessories;
	readonly promotionalSkus: PromotionalSkus;
	readonly artifacts: Artifacts;
	readonly material: KitchenMaterial;
	readonly design: KitchenDesign;
	readonly skuAnnotations: SkuAnnotations;
	readonly blueprintAnnotations: SkuAnnotations;

	#refMap: InternalReferenceMap;

	#isLocal: boolean;
	#lastMutatedAt: number;
	public isTemp: boolean;

	constructor (options: BlueprintOptions<KitchenDataIn> = {}, context: BlueprintContext | null = null) {
		super(BlueprintInstanceType.Kitchen, options, context);
		const data = kitchenData(this.context, options?.dataIn ?? null);

		this.#refMap = {
			...emptyInternalReferenceMapCfn(),
			kitchen: this,
			type: BlueprintInstanceType.Kitchen
		};

		this.#id = data.id;
		this.#createdBy = data.createdBy;
		this.#createDate = data.createDate;
		this.#version = data.version;
		this.#baseVersion = data.baseVersion;
		this.material = new KitchenMaterial({ dataIn: data.material, parent: this });
		this.#fuelchoice = data.fuelchoice;
		this.#skyKey = data.skyKey;
		this.#floorKey = data.floorKey;
		this.#designPackageImage = data.designPackageImage;
		this.design = new KitchenDesign(this, { dataIn: data.design, parent: this });
		this.accessories = new Accessories({ dataIn: data.accessories, parent: this })
		this.promotionalSkus = new PromotionalSkus({ dataIn: data.promotionalSkus, parent: this });
		this.artifacts = new Artifacts(data.artifacts, this);
		this.#tax = data.tax;
		this.#description = data.description;
		this.#kitchenLayout = data.kitchenLayout;
		this.#layout = data.layout;
		this.#location = data.location;
		this.notes = new Notes(this.context.userEmail, { dataIn: data.notes });
		this.skuAnnotations = new SkuAnnotations(this.context.userEmail, { dataIn: data.skuAnnotations });
		this.blueprintAnnotations = new SkuAnnotations(this.context.userEmail, { dataIn: data.blueprintAnnotations });

		this.#quoteId = data.quoteId;
		this.#isFinalized = data.isFinalized;
		this.#dealId = data.dealId;
		this.#priceBookVersionId = data.priceBookVersionId;
		this.#priceBookPinnedBy = data.priceBookPinnedBy;
		this.#priceBookRuleId = data.priceBookRuleId;
		this.#designDetails = data.designDetails;
		this.#blueprintOverrides = data.blueprintOverrides;
		this.#blueprintOverrideBases = data.blueprintOverrideBases;

		this.#rulesEngine = new RulesEngine(this);

		this.#isLocal = false;
		this.#lastMutatedAt = 0;
		this.isTemp = false;

		// pass all events to the cache invalidator
		this.subscribe('*', '*', (event) => this.context.cache.invalidateEvent(event.noun, event.action));

		this.material.subscribe('*', '*', (event) => {
			const e = event;
			if (!e.payload.ref) e.payload.ref = this.#id;
			this.publishEvent(e);
		});
		this.notes.subscribe('*', '*', (event) => this.publishEvent(event));
		this.skuAnnotations.subscribe('*', '*', (event) => this.publishEvent(event));
		this.blueprintAnnotations.subscribe('*', '*', (event) => this.publishEvent(event));
		this.setInitialized();

		// auto-overwrite design detail fields when source properties change
		this.material.subscribe('surround_material', 'updated', () => {
			if (this.#designDetails?.panelFinish) {
				this.#designDetails = { ...this.#designDetails, panelFinish: { value: this.material.surround.displayName, isCustom: false } };
				this.publishEvent({ noun: 'kitchen_design_details', action: 'updated', payload: { ref: this.#id } });
			}
		});
		this.material.subscribe('countertop_material', 'updated', () => {
			if (this.#designDetails?.countertop) {
				this.#designDetails = { ...this.#designDetails, countertop: { value: this.material.countertop.displayName, isCustom: false } };
				this.publishEvent({ noun: 'kitchen_design_details', action: 'updated', payload: { ref: this.#id } });
			}
		});
		this.subscribe('kitchen_fuel', 'updated', () => {
			if (this.#designDetails?.fuel) {
				this.#designDetails = { ...this.#designDetails, fuel: { value: this.#fuelchoice?.toUpperCase(), isCustom: false } };
				this.publishEvent({ noun: 'kitchen_design_details', action: 'updated', payload: { ref: this.#id } });
			}
		});
	}

	get id (): string {
		return this.#id;
	}
	get baseVersion (): number | null {
		return this.#baseVersion;
	}
	get createdBy (): string {
		return this.#createdBy;
	}
	get createDate (): Date {
		return new Date(this.#createDate === '' || isNaN(Number(this.#createDate)) ? this.#createDate : Number(this.#createDate));
	}

	@Publish('kitchen_fuel', 'updated')
	set fuelchoice (fuelchoice: string) {
		this.#fuelchoice = fuelchoice;
		rebindFuelVariantsCfn(this as Kitchen);
	}
	get fuelchoice (): string {
		return this.#fuelchoice;
	}

	/** Derived from every island's PanelTrims; the setter converts them all. Each conversion publishes its own `panel_trim/updated`. */
	set trimProfile (trimProfile: KitchenTrimProfile) {
		setTrimProfileCfn(this as Kitchen, trimProfile);
	}
	get trimProfile (): KitchenTrimProfile {
		return getTrimProfileCfn(this as Kitchen);
	}

	@Publish('kitchen_sky', 'updated')
	set skyKey (value: string) {
		this.#skyKey = value;
	}
	get skyKey (): string {
		return this.#skyKey;
	}

	@Publish('kitchen_floor', 'updated')
	set floorKey (value: string) {
		this.#floorKey = value;
	}
	get floorKey (): string {
		return this.#floorKey;
	}

	set designPackageImage (value: string) {
		this.#designPackageImage = value;
	}
	get designPackageImage (): string {
		return this.#designPackageImage;
	}

	get refMap (): ReferenceMap { return resolveReferenceMapCfn(this.#refMap); }
	get size (): KitchenSize {
		return getSizeCfn(this as Kitchen, true);
	}
	get version (): number | null {
		return this.#version;
	}
	get itemizedMsrp (): KitchenItemizedMsrp {
		return getItemizedMsrpCfn(this as Kitchen);
	}

	/**
	 * Canonical estimate rows consumed by every estimate surface (panel, PDF, design package)
	 */
	get estimateLineItems (): Array<KitchenEstimateLineItem> {
		return getEstimateLineItemsCfn(this as Kitchen);
	}

	@Publish('kitchen_is_local', 'updated')
	set isLocal (isLocal: boolean) {
		this.#isLocal = isLocal;
	}
	get isLocal (): boolean {
		return this.#isLocal;
	}

	/**
	 * Epoch ms of the last design mutation, 0 when nothing has been mutated since this instance was
	 * built. Deliberately NOT serialized and NOT published: it is in-memory session truth used to
	 * decide whether a captured rendering still matches the design, and publishing it would re-enter
	 * the mutation subscription that writes it.
	 */
	set lastMutatedAt (lastMutatedAt: number) {
		this.#lastMutatedAt = lastMutatedAt;
	}
	get lastMutatedAt (): number {
		return this.#lastMutatedAt;
	}

	get state (): string {
		// console.log('kitchen:state', this.context.blueprint.kitchens.map((k) => k.version));
		// console.log('kitchen:state', { version: this.#version, baseVersion: this.#baseVersion, isFinalized: this.#isFinalized, QuiteId: this.#quoteId });
		// A quote does NOT freeze a design — designers quote while edits are still pending — so
		// quoteId deliberately reports no state here.
		if (this.#isFinalized) return 'Finalized (Read-Only)';
		if (this.#version === null) return 'Draft';
		return '';
	}

	@Publish('kitchen_description', 'updated')
	set description (description: string) {
		this.#description = description;
	}
	get description (): string {
		if (this.#description === null || this.#description.trim() === '') {
			this.#description = getDescriptionCfn(this as Kitchen, this.#description);
		}
		return this.#description;
	}

	@Publish('kitchen_kitchen_layout', 'updated')
	set kitchenLayout (layout: string) {
		this.#kitchenLayout = layout;
	}
	get kitchenLayout (): string {
		return this.#kitchenLayout;
	}

	@Publish('kitchen_layout', 'updated')
	set layout (layout: string) {
		this.#layout = Object.values(kitchenLayouts).includes(layout) ? layout : kitchenLayouts.Custom;
	}
	get layout (): KitchenLayouts {
		return this.#layout;
	}

	@Publish('kitchen_location', 'updated')
	set location (location: string) {
		this.#location = location;
	}
	get location (): string {
		return this.#location;
	}

	@Publish('kitchen_tax', 'updated')
	set tax (tax: number) {
		this.#tax = tax;
	}
	get tax (): number {
		return this.#tax;
	}

	@Publish('kitchen_quote_id', 'updated')
	set quoteId (quoteId: string) {
		this.#quoteId = quoteId;
	}
	get quoteId (): string {
		return this.#quoteId;
	}

	@Publish('kitchen_is_finalized', 'updated')
	set isFinalized (isFinalized: boolean) {
		this.#isFinalized = isFinalized;
	}
	get isFinalized (): boolean {
		return this.#isFinalized;
	}

	@Publish('kitchen_deal_id', 'updated')
	set dealId (dealId: string) {
		this.#dealId = dealId;
	}
	get dealId (): string {
		return this.#dealId;
	}

	@Publish('kitchen_price_book', 'updated')
	set priceBookVersionId (priceBookVersionId: string | null) {
		this.#priceBookVersionId = priceBookVersionId;
	}
	get priceBookVersionId (): string | null {
		return this.#priceBookVersionId;
	}

	@Publish('kitchen_price_book', 'updated')
	set priceBookPinnedBy (priceBookPinnedBy: PriceBookPinnedBy | null) {
		this.#priceBookPinnedBy = priceBookPinnedBy;
	}
	get priceBookPinnedBy (): PriceBookPinnedBy | null {
		return this.#priceBookPinnedBy;
	}

	@Publish('kitchen_price_book', 'updated')
	set priceBookRuleId (priceBookRuleId: string | null) {
		this.#priceBookRuleId = priceBookRuleId;
	}
	get priceBookRuleId (): string | null {
		return this.#priceBookRuleId;
	}

	@Publish('kitchen_design_details', 'updated')
	set designDetails (designDetails: DesignDetailsData | null) {
		this.#designDetails = designDetails;
	}
	get designDetails (): DesignDetailsData | null {
		return this.#designDetails;
	}

	@Publish('kitchen', 'updated')
	set blueprintOverrides (blueprintOverrides: Record<string, string>) {
		this.#blueprintOverrides = blueprintOverrides;
	}
	get blueprintOverrides (): Record<string, string> {
		return this.#blueprintOverrides;
	}

	@Publish('kitchen', 'updated')
	set blueprintOverrideBases (blueprintOverrideBases: Record<string, string>) {
		this.#blueprintOverrideBases = blueprintOverrideBases;
	}
	get blueprintOverrideBases (): Record<string, string> {
		return this.#blueprintOverrideBases;
	}

	/** The generated content the override under `key` was last edited against, or undefined for a legacy override saved before base-capture existed. */
	public getBlueprintOverrideBase (key: string): string | undefined {
		return this.#blueprintOverrideBases[key];
	}

	/**
	 * Sets a single blueprint override value for the given key
	 * @param key stable identifier for the field (e.g. 'header.customerName')
	 * @param value the overridden text
	 * @param base the generated content this edit was made against — the merge base for automation/manual reconciliation. Omit to leave any existing base untouched.
	 */
	@Publish('kitchen', 'updated')
	public setBlueprintOverride (key: string, value: string, base?: string): void {
		this.#blueprintOverrides = { ...this.#blueprintOverrides, [key]: value };
		if (base !== undefined) this.#blueprintOverrideBases = { ...this.#blueprintOverrideBases, [key]: base };
	}

	/**
	 * Removes a single blueprint override for the given key
	 * @param key stable identifier for the field to clear
	 */
	@Publish('kitchen', 'updated')
	public clearBlueprintOverride (key: string): void {
		const { [key]: _removed, ...rest } = this.#blueprintOverrides;
		this.#blueprintOverrides = rest;
		const { [key]: _removedBase, ...restBases } = this.#blueprintOverrideBases;
		this.#blueprintOverrideBases = restBases;
	}

	/**
	 * Updates the customerName field in designDetails, resetting isCustom to false
	 * @param name the new customer name
	 */
	public setDesignDetailCustomerName (name: string): void {
		const designDetails = this.#designDetails;
		if (!designDetails?.customerName) return;

		const { value, isCustom } = designDetails.customerName;
		// re-published on every design load, so a no-op write must stay silent or it reads as a user edit
		if (value === name && !isCustom) return;

		this.#designDetails = { ...designDetails, customerName: { value: name, isCustom: false } };
		this.publishEvent({ noun: 'kitchen_design_details', action: 'updated', payload: { ref: this.#id } });
	}

	// calculated properties
	/**
	 * kitchen center represents the position relative to the default island
	 * For kitchen design: center and origin are always the same (both relative to default island at 0,0,0 world space)
	 * @returns Vertex3 representing the center position relative to the default island
	 */
	get center (): Vertex3 {
		return this.size.origin;
	}

	/**
	 * kitchen origin represents the world space position of the kitchen
	 * For kitchen design: origin = center (both relative to default island at 0,0,0 world space)
	 * @returns Vertex3 representing the world space position
	 */
	get origin (): Vertex3 {
		// For kitchen design, center and origin are always the same
		return this.center;
	}

	/**
	 * cleans up resources when kitchen is deleted
	 */
	public cleanup (): void {
		this.design.cleanup();
		super.cleanup();
	}

	/**
	 * creates a copy of the kitchen, resets version, base version
	 * @param perserveContext creates new Kitchen using current context
	 * @returns Kitchen
	 */
	public clone (perserveContext = false): Kitchen {
		const thisData: KitchenDataOut = this.serialize();
		thisData.version = null;
		thisData.baseVersion = this.#version;
		delete thisData.id;
		delete thisData.createdBy;
		delete thisData.createDate;
		return new Kitchen({ dataIn: thisData }, perserveContext ? this.context : null);
	}

	/**
	 * locates specified id in the kitchen tree and returns a reference map for that id
	 * @param id id of the class instance to find
	 * @returns ReferenceMap: contains item matching and parent links
	 */
	public getReferenceMap (id: string): ReferenceMap {
		const cached = this.context.cache.match<ReferenceMap>(id);
		if (cached) return cached;

		// console.log('getReferenceMap (find from root)', id, this);

		// is it a countertop or countertop sub item?
		let referenceMap = this.context.cache.match<ReferenceMap>(id, () => ['refMap', this.design.countertops.getReferenceMap(id)]);

		// not a countertop, then maybe islands or island sub-item
		if (referenceMap === null) {
			referenceMap = this.context.cache.match<ReferenceMap>(id, () => ['refMap', this.design.islands.getReferenceMap(id)]);
			// console.log('getReferenceMap', referenceMap, id, this.design.islands);
		}

		// not an island, then maybe obstructions or obstruction sub-item
		if (referenceMap === null) {
			referenceMap = this.context.cache.match<ReferenceMap>(id, () => ['refMap', this.design.obstructions.getReferenceMap(id)]);
		}

		// if (referenceMap) console.log('getReferenceMap (found)', referenceMap);
		return referenceMap;
	}

	/**
	 * locates specified id in the kitchen tree and returns the item type and item itself
	 * @param id id of the class instance to find
	 * @returns tuple containing the item type and item itself
	 * @example
	 * const [type, item] = kitchen.getReferenceItem('[ITEM-GUID]');
	 */
	public getReferenceItem (id: string): [string, ReferenceMapItemType] {
		const refMap = this.getReferenceMap(id);
		if (!refMap?.type) return [null, null];
		return [refMap.type, refMap[toCamelCase(refMap.type)]];
	}

	/**
	 * Calculates and returns the size of the kitchen based on all islands and their connections
	 * @param includeCountertop when true, includes countertop overhangs in the calculation
	 * @returns KitchenSize
	 */
	public getSize (includeCountertop = true): KitchenSize {
		const cacheKey = includeCountertop ? 'getSize:ct' : 'getSize';
		return this.context.cache.match<KitchenSize>([this.#id, cacheKey], () => [cacheKey, getSizeCfn(this as Kitchen, includeCountertop)], {
			events: [['island_size', 'updated'], ['connections', 'created'], ['island', 'deleted'], ['connections', 'deleted'], ['overhang', 'updated'], ['surround_material', 'updated'], ['panel_size', '*']]
		});
	}

	/**
	 * Serializes the class instance
	 * @returns KitchenDataOut
	 */
	public serialize (): KitchenDataOut {
		return {
			id: this.#id,
			version: this.#version,
			baseVersion: this.#baseVersion,
			createdBy: this.#createdBy,
			createDate: this.#createDate,
			material: this.material.serialize(),
			fuelchoice: this.#fuelchoice,
			skyKey: this.#skyKey,
			floorKey: this.#floorKey,
			designPackageImage: this.#designPackageImage,
			design: this.design.serialize(),
			accessories: this.accessories.serialize(),
			promotionalSkus: this.promotionalSkus.serialize(),
			// artifacts: this.artifacts?.serialize() ?? null,
			notes: this.notes.serialize(),
			skuAnnotations: this.skuAnnotations.serialize(),
			blueprintAnnotations: this.blueprintAnnotations.serialize(),
			quoteId: this.#quoteId,
			isFinalized: this.#isFinalized,
			tax: this.#tax,
			description: this.#description,
			kitchenLayout: this.#kitchenLayout,
			layout: this.#layout,
			location: this.#location,
			dealId: this.#dealId,
			priceBookVersionId: this.#priceBookVersionId,
			priceBookPinnedBy: this.#priceBookPinnedBy,
			priceBookRuleId: this.#priceBookRuleId,
			designDetails: this.#designDetails,
			blueprintOverrides: this.#blueprintOverrides,
			blueprintOverrideBases: this.#blueprintOverrideBases
		};
	}

	/* ************** */
	/* Static Methods */
	/* ************** */

	/**
	 *
	 * @returns KitchenLayouts
	 */
	static get layoutOptions (): string[] {
		return Object.keys(kitchenLayouts);
	}

	/**
	 *
	 * @returns empty ReferenceMap
	 */
	static emptyReferenceMap (): ReferenceMap {
		return emptyReferenceMapCfn();
	}
}
